import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Languages, Plus, Save, X, Check, ChevronDown, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { DeclarationInformation } from './DeclarationSummary/DeclarationInformation';
import { updateWorkflowSessionState } from '../../store/reducers';
import { ItemInformation } from './DeclarationSummary/ItemInformation';
import { DocumentsTable } from './DeclarationSummary/DocumentsTable';
import { PackagesTable } from './DeclarationSummary/PackagesTable';
import { ValidationErrors } from './DeclarationSummary/ValidationErrors';
import { DeclarationDocumentsTable } from './DeclarationSummary/DeclarationDocumentsTable';
import { useDeclarationSummary } from '../../hooks/useDeclarationSummary';
import { getDeclarationSummaryTranslation, getMainTranslation } from '../../hooks/useLanguage';
import { getDeclarationKind } from '../../utils/declarationKindUtils';
import { aggregateDocumentosFromPartidas, createPartidasDocumentosSignature } from '../../utils/declarationSummaryUtils';
import { addPartida } from '../../utils/editableDataUpdates';
import { deepClone } from '../../utils/dataHelpers';
import { useMemo } from 'react';
import { useDeclarationBreadcrumb } from '../../contexts/DeclarationBreadcrumbContext';

export const DeclarationSummary = ({ jsonData, isSummaryExpanded, selectedOption }) => {
  const [activeDeclarationIndex, setActiveDeclarationIndex] = useState(-1); // -1 means "All"
  const [activeItemIndices, setActiveItemIndices] = useState({}); // Object mapping declarationIndex to itemIndex
  const {
    isSpanish,
    setIsSpanish,
    editableData,
    setEditableData,
    isDirty,
    setIsDirty,
    validationErrors,
    handleChange,
    saveChanges,
    cancelChanges,
    countTotalItems
  } = useDeclarationSummary(jsonData);

  const handleSaveClick = () => {
    const result = saveChanges();
    if (result?.success === false) {
      toast.error(getMainTranslation('fixAllRequiredBeforeSave', isSpanish));
    } else {
      toast.success(getMainTranslation('changesSaved', isSpanish));
    }
  };

  const handleCancelClick = () => {
    cancelChanges();
    toast.success(getMainTranslation('changesCancelled', isSpanish));
  };

  // Reset active declaration index if it's out of bounds (but keep -1 for "All")
  useEffect(() => {
    // Validate editableData
    if (!editableData || !Array.isArray(editableData) || editableData.length === 0) {
      // If no data, reset to "All"
      if (activeDeclarationIndex !== -1) {
        setActiveDeclarationIndex(-1);
      }
      return;
    }
    
    // Validate activeDeclarationIndex is within bounds
    if (activeDeclarationIndex >= editableData.length && activeDeclarationIndex !== -1) {
      setActiveDeclarationIndex(-1); // Reset to "All" if out of bounds
    }
  }, [editableData, activeDeclarationIndex]);

  // Helper function to get active item index for a declaration
  const getActiveItemIndex = (declarationIndex) => {
    return activeItemIndices[declarationIndex] ?? -1;
  };

  const setDeclarationBreadcrumb = useDeclarationBreadcrumb()?.setDeclarationBreadcrumb;
  const activeItemIndexForBreadcrumb = activeDeclarationIndex >= 0 ? getActiveItemIndex(activeDeclarationIndex) : -1;

  useEffect(() => {
    if (typeof setDeclarationBreadcrumb !== 'function') return;
    setDeclarationBreadcrumb(activeDeclarationIndex, activeItemIndexForBreadcrumb);
  }, [activeDeclarationIndex, activeItemIndexForBreadcrumb, setDeclarationBreadcrumb]);

  // Helper function to set active item index for a declaration
  const setActiveItemIndex = (declarationIndex, itemIndex) => {
    setActiveItemIndices(prev => ({
      ...prev,
      [declarationIndex]: itemIndex
    }));
  };

  const dispatch = useDispatch();
  const activeWorkflowSessionId = useSelector((state) => state.workflowSessions?.activeSessionId);
  const declarationSummaryCollapsedByIndex = useSelector((state) => {
    const id = state.workflowSessions?.activeSessionId;
    return id ? state.workflowSessions?.sessions?.[id]?.state?.declarationSummaryCollapsedByIndex : undefined;
  }) || {};
  const collapsedExportByIndex = declarationSummaryCollapsedByIndex;

  const toggleExportCollapsed = (declarationIndex) => {
    if (!activeWorkflowSessionId) return;
    const next = { ...declarationSummaryCollapsedByIndex, [declarationIndex]: !declarationSummaryCollapsedByIndex[declarationIndex] };
    dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { declarationSummaryCollapsedByIndex: next }));
  };

  const onExpandExport = (declarationIndex) => {
    if (!activeWorkflowSessionId) return;
    const next = { ...declarationSummaryCollapsedByIndex, [declarationIndex]: false };
    dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { declarationSummaryCollapsedByIndex: next }));
  };

  // State for declaration-level documentos toggle (per declaration)
  const [hasDeclarationDocuments, setHasDeclarationDocuments] = useState({});
  const [removeDuplicates, setRemoveDuplicates] = useState({});
  const prevPartidasDocumentosRefs = useRef({});
  const isManualEditRefs = useRef({});
  const prevEditableDataLengthRef = useRef(editableData?.length || 0);

  // Handle toggle for declaration-level documentos
  const handleToggleDeclarationDocuments = (declarationIndex) => {
    const declaration = editableData[declarationIndex];
    if (!declaration) return;

    const updatedData = deepClone(editableData);
    const currentState = hasDeclarationDocuments[declarationIndex] || false;
    const newState = !currentState;
    
    if (newState) {
      // Enable: aggregate documentos from all partidas
      const removeDups = removeDuplicates[declarationIndex] || false;
      const aggregated = aggregateDocumentosFromPartidas(declaration, removeDups);
      updatedData[declarationIndex].documentos = aggregated;
      // Update the ref signature to prevent immediate re-sync
      if (declaration.partidas) {
        prevPartidasDocumentosRefs.current[declarationIndex] = createPartidasDocumentosSignature(declaration);
      }
    } else {
      // Disable: clear declaration-level documentos
      updatedData[declarationIndex].documentos = [];
    }
    
    setEditableData(updatedData);
    setIsDirty(true);
    setHasDeclarationDocuments(prev => ({ ...prev, [declarationIndex]: newState }));
    isManualEditRefs.current[declarationIndex] = true; // Mark as manual edit to prevent auto-sync
  };

  // Handle remove duplicates toggle
  const handleToggleRemoveDuplicates = (declarationIndex) => {
    const declaration = editableData[declarationIndex];
    if (!declaration) return;

    const currentState = removeDuplicates[declarationIndex] || false;
    const newState = !currentState;
    setRemoveDuplicates(prev => ({ ...prev, [declarationIndex]: newState }));
    
    if (hasDeclarationDocuments[declarationIndex]) {
      // Re-aggregate with new duplicate removal setting
      const updatedData = deepClone(editableData);
      const aggregated = aggregateDocumentosFromPartidas(declaration, newState);
      updatedData[declarationIndex].documentos = aggregated;
      setEditableData(updatedData);
      setIsDirty(true);
      isManualEditRefs.current[declarationIndex] = true; // Mark as manual edit to prevent auto-sync
    }
  };

  // Sync declaration documentos when partidas change - optimized with signature comparison
  useEffect(() => {
    if (!editableData || !Array.isArray(editableData)) return;

    const lengthChanged = editableData.length !== prevEditableDataLengthRef.current;
    prevEditableDataLengthRef.current = editableData.length;

    editableData.forEach((declaration, declarationIndex) => {
      if (!declaration) return;

      if (isManualEditRefs.current[declarationIndex]) {
        isManualEditRefs.current[declarationIndex] = false;
        return;
      }
      
      if (!hasDeclarationDocuments[declarationIndex] || !declaration.partidas) {
        return;
      }
      
      const currentSignature = createPartidasDocumentosSignature(declaration);
      const prevSignature = prevPartidasDocumentosRefs.current[declarationIndex];
      
      if (prevSignature === currentSignature && !lengthChanged) {
        return;
      }
      
      prevPartidasDocumentosRefs.current[declarationIndex] = currentSignature;
      const updatedData = deepClone(editableData);
      const removeDups = removeDuplicates[declarationIndex] || false;
      const aggregated = aggregateDocumentosFromPartidas(declaration, removeDups);
      updatedData[declarationIndex].documentos = aggregated;
      setEditableData(updatedData);
      setIsDirty(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableData, hasDeclarationDocuments, removeDuplicates]);

  // Initialize toggle states based on declaration state
  useEffect(() => {
    if (!editableData || !Array.isArray(editableData)) return;

    const newStates = {};
    const newRemoveDups = {};
    editableData.forEach((declaration, index) => {
      if (declaration) {
        const hasDocs = declaration.documentos && Array.isArray(declaration.documentos) && declaration.documentos.length > 0;
        newStates[index] = hasDocs;
        // Initialize removeDuplicates to false if not set
        if (!(index in removeDuplicates)) {
          newRemoveDups[index] = false;
        } else {
          newRemoveDups[index] = removeDuplicates[index];
        }
      }
    });
    setHasDeclarationDocuments(prev => ({ ...prev, ...newStates }));
    setRemoveDuplicates(prev => ({ ...prev, ...newRemoveDups }));
  }, [editableData?.length]);

  // Reset active item indices when items change
  useEffect(() => {
    // Validate editableData
    if (!editableData || !Array.isArray(editableData) || editableData.length === 0) {
      return;
    }
    
    // Use functional update to access current state without needing it in dependencies
    setActiveItemIndices(prev => {
      const newIndices = {};
      let hasChanges = false;
      
      editableData.forEach((declaration, declarationIndex) => {
        // Validate declaration structure
        if (!declaration || typeof declaration !== 'object') {
          return; // Skip invalid declarations
        }
        
        // Validate partidas array
        const itemsCount = (declaration.partidas && Array.isArray(declaration.partidas)) 
          ? declaration.partidas.length 
          : 0;
        
        const currentIndex = prev[declarationIndex] ?? -1;
        if (currentIndex === -1) {
          // Preserve "All" selection - no change needed
          return;
        } else if (currentIndex >= itemsCount) {
          // Reset to "All" if out of bounds
          newIndices[declarationIndex] = -1;
          hasChanges = true;
        } else if (currentIndex >= 0 && currentIndex < itemsCount) {
          // Keep current if valid - no change needed
          return;
        }
      });
      
      // Only update if there are actual changes
      if (hasChanges) {
        return { ...prev, ...newIndices };
      }
      return prev; // Return same reference if no changes
    });
  }, [editableData]); // Only depend on editableData

  // CRITICAL: Memoize declarations to preserve object references This prevents cascading re-renders when editableData array reference changes but individual declaration objects haven't changed
  const declarationRefs = useRef(new Map());
  const memoizedDeclarationsWithIndices = useMemo(() => {
    if (!editableData || !Array.isArray(editableData)) return [];
    
    const result = [];
    editableData.forEach((declaration, declarationIndex) => {
      if (declaration === null || declaration === undefined || typeof declaration !== 'object') {
        return;
      }
      
      // Use stable key based on declaration index and expediente
      const key = `${declarationIndex}_${declaration?.expediente || ''}`;
      
      // Check if we have a cached declaration object for this key
      let cached = declarationRefs.current.get(key);
      
      // Only create new object if declaration reference actually changed OR if we don't have a cached version
      if (!cached || cached.declaration !== declaration) {
        cached = { declaration, declarationIndex };
        declarationRefs.current.set(key, cached);
      } else {
        // Update declarationIndex in case it changed (shouldn't happen, but be safe)
        cached.declarationIndex = declarationIndex;
      }
      
      result.push(cached);
    });
    
    // Clean up old refs that are no longer in editableData
    const currentKeys = new Set(result.map(r => `${r.declarationIndex}_${r.declaration?.expediente || ''}`));
    for (const [key] of declarationRefs.current) {
      if (!currentKeys.has(key)) {
        declarationRefs.current.delete(key);
      }
    }
    
    return result;
  }, [editableData]); // Depend on editableData, but use refs to preserve object references

  return (
    <div className="declaration-summary">
      <div className="">
        <div className={`${selectedOption === "split" && "sticky"} top-0 bg-white pt-4 w-full z-999`}>
          <div className=" gap-2  bg-white flex-col flex">
            <div className='justify-between gap-2 items-center flex bg-white'>
            <h2 className={`${isSummaryExpanded ? "text-lg" : "text-lg"} font-semibold`}>
              {getDeclarationSummaryTranslation('declarationSummary', isSpanish)}
            </h2>
            <div className="flex items-center gap-2">
              {/* Only show save/cancel buttons in split view - Main.js has them for non-split view */}
              {isDirty && selectedOption === "split" && (
                <div className="flex gap-2">
                  <button
                    className="bg-primary rounded-full flex items-center py-2 px-3 gap-2"
                    onClick={handleSaveClick}
                    title="Save Changes"
                  >
                    <Save className="size-4" />
                    <span className="text-xs">{getDeclarationSummaryTranslation('save', isSpanish)}</span>
                  </button>
                  <button
                    className="bg-red-50 rounded-full flex items-center py-2 px-3 gap-2"
                    onClick={handleCancelClick}
                    title="Cancel Changes"
                  >
                    <X className="stroke-red-500 size-4" />
                    <span className="text-red-500 text-xs">{getDeclarationSummaryTranslation('cancel', isSpanish)}</span>
                  </button>
                </div>
              )}
              {/* <button
                className={`flex items-center gap-2 py-2 px-3 rounded-full ${isSpanish ? "bg-default" : "bg-bgGray"}`}
                title="Language Toggle"
                onClick={() => { setIsSpanish(!isSpanish) }}
              >
                <Languages className={`size-4 ${isSpanish && "stroke-white"}`} />
                <p className={`text-xs ${isSpanish && "text-white"}`}>{isSpanish ? "ESP" : "ENG"}</p>
              </button> */}
            </div>
            </div>
            {selectedOption === "split" && validationErrors && validationErrors.length > 0 && (
              <div id="validation-errors" className="max-h-64 overflow-y-scroll">
                <ValidationErrors validationErrors={validationErrors} isSpanish={isSpanish} splitView={selectedOption === "split"} onExpandExport={onExpandExport}/>
              </div>
            )}
          </div>
        </div>

        {isSummaryExpanded && (
          <div className="mt-2">
            <div>
              <div className="grid grid-cols-2 gap-4 split-modal-header">
                <div className="form-group flex-1">
                  <label htmlFor="companyName">{getDeclarationSummaryTranslation('totalDeclarations', isSpanish)}</label>
                  <p className="font-bold text-4xl">{editableData.length || 0}</p>
                </div>
                <div className="form-group flex-1">
                  <label htmlFor="companyName">{getDeclarationSummaryTranslation('itemsCount', isSpanish)}</label>
                  <p className="font-bold text-4xl">{countTotalItems()}</p>
                </div>
              </div>
            </div>

            {/* Declaration Tabs Container */}
            {Array.isArray(editableData) && editableData.length > 0 && (
                <div className="border-ta pt-4 mt-4 overflow-x-auto">
                  <div className="flex gap-2 items-center pb-2">
                    {/* All Tab */}
                    <button
                      onClick={() => setActiveDeclarationIndex(-1)}
                      className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                        activeDeclarationIndex === -1
                          ? 'bg-secondary font-semibold px-4'
                          : 'pr-2'
                      }`}
                    >
                      {isSpanish ? 'Todos' : 'All'}
                    </button>
                    {/* Individual Declaration Tabs */}
                    {memoizedDeclarationsWithIndices.length > 0 && memoizedDeclarationsWithIndices
                      .map(({ declaration, declarationIndex }) => (
                        <button
                          key={declarationIndex}
                          onClick={() => setActiveDeclarationIndex(declarationIndex)}
                          className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 px-4 whitespace-nowrap flex-shrink-0 ${
                            activeDeclarationIndex === declarationIndex
                              ? 'bg-secondary font-semibold'
                              : ''
                          }`}
                        >
                          {getDeclarationSummaryTranslation(getDeclarationKind(declaration), isSpanish)} #{declarationIndex + 1}
                        </button>
                      ))}
                  </div>
                </div>
              )}

            {/* Show declarations based on active tab */}
            {memoizedDeclarationsWithIndices.length > 0 && (
              activeDeclarationIndex === -1 
                ? // Show all declarations
                  memoizedDeclarationsWithIndices
                    .map(({ declaration, declarationIndex }) => {
                      // Use stable key to prevent React from re-mounting
                      const stableKey = `declaration-${declarationIndex}-${declaration?.expediente || declarationIndex}`;
                      const exportLabel = `${getDeclarationSummaryTranslation(getDeclarationKind(declaration), isSpanish)} #${declarationIndex + 1}`;
                      return (
                        <div key={stableKey} id={`section-declaration-summary-export-${declarationIndex}`} data-nav-breadcrumb={exportLabel} className={`mt-4 mb-2 border-t pt-4 first:border-t-0 first:pt-0`}>
                      <div className={`flex items-center justify-between mb-4 ${collapsedExportByIndex[declarationIndex] ? 'w-fit gap-4' : 'w-full'}`}>
                      <h2 className={`text-4xl font-semibold split-header uppercase text-template-primary ${!declaration.expediente && 'text-red-600'}`}>
                        {getDeclarationSummaryTranslation(getDeclarationKind(declaration), isSpanish)} #{declarationIndex + 1}
                      </h2>

                      {/* Declaration-Level Documents Toggle */}
                      <div className='flex items-center gap-1'>
                      {!collapsedExportByIndex[declarationIndex] && (
                      <div className="">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 ">
                            <button
                              type="button"
                              onClick={() => handleToggleDeclarationDocuments(declarationIndex)}
                              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                                hasDeclarationDocuments[declarationIndex]
                                  ? 'bg-primary text-white'
                                  : 'bg-secondary text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {hasDeclarationDocuments[declarationIndex] && (
                                <Check className="w-4 h-4" />
                              )}
                              <span>
                                {!isSpanish ? "Add Documents at Export Level" : "Agregar Documentos en Nivel de Exportación"} 
                              </span>
                            </button>
                            {(hasDeclarationDocuments[declarationIndex]) && (
                              <button
                                type="button"
                                onClick={() => handleToggleRemoveDuplicates(declarationIndex)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                                  removeDuplicates[declarationIndex]
                                    ? 'bg-secondary'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {removeDuplicates[declarationIndex] && (
                                  <Check className="w-4 h-4" />
                                )}
                                <span>
                                  {!isSpanish ? "Remove Duplicates" : "Eliminar Duplicados"}
                                </span>
                              </button>
                            )}
                          </div>
                          {(hasDeclarationDocuments[declarationIndex]) && (
                            <span className="text-sm text-gray-600 ml-2">
                              {declaration.documentos?.length || 0} {!isSpanish ? "documents" : "documentos"}
                            </span>
                          )}
                        </div>
                      </div>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleExportCollapsed(declarationIndex)}
                        className={`flex-shrink-0 p-1 rounded-full bg-gray-100 transition-colors ${collapsedExportByIndex[declarationIndex] ? 'mt-1' : ''}`}
                        title={collapsedExportByIndex[declarationIndex] ? (isSpanish ? 'Expandir' : 'Expand') : (isSpanish ? 'Colapsar' : 'Collapse')}
                        aria-expanded={!collapsedExportByIndex[declarationIndex]}
                      >
                        {collapsedExportByIndex[declarationIndex] ? (
                          <ChevronRight className="size-5 text-gray-600" />
                        ) : (
                          <ChevronDown className="size-5 text-gray-600" />
                        )}
                      </button>
                      </div>
                      </div>

                      {!collapsedExportByIndex[declarationIndex] && (
                      <>
                      <DeclarationInformation 
                        handleChange={handleChange} 
                        declaration={declaration} 
                        declarationIndex={declarationIndex} 
                        isSpanish={isSpanish}
                        setEditableData={setEditableData}
                        setIsDirty={setIsDirty}
                      />

                      {/* Declaration-Level Documents */}
                      {declaration.documentos && Array.isArray(declaration.documentos) && declaration.documentos.length > 0 && (
                        <DeclarationDocumentsTable
                          editableData={editableData}
                          setEditableData={setEditableData}
                          setIsDirty={setIsDirty}
                          declarationIndex={declarationIndex}
                          isSpanish={isSpanish}
                          declaration={declaration}
                        />
                      )}

                      {/* Items (Partidas) */}
                      <div className="form-group flex-1 mt-12 !mb-0 flex justify-between flex-row items-center">
                        <label htmlFor="partidas" className="!mb-0 text-3xl font-semibold uppercase">{getDeclarationSummaryTranslation('items', isSpanish)} ({declaration.partidas?.length || 0})</label>
                        <button
                          className="py-1 text-xs flex items-center gap-1"
                          onClick={() => {
                            const updatedData = addPartida(editableData, declarationIndex);
                            if (updatedData !== editableData) {
                              setEditableData(updatedData);
                              setIsDirty(true);
                            }
                          }}
                        >
                          <Plus className={`size-4`} />
                          {getDeclarationSummaryTranslation('addItem', isSpanish)}
                        </button>
                      </div>

                      {/* Item Tabs Container */}
                      {declaration.partidas && declaration.partidas.length > 0 && (
                        <div className="mt-4 overflow-x-auto">
                          <div className="flex gap-2 items-center pb-2">
                            {/* All Tab */}
                            <button
                              onClick={() => setActiveItemIndex(declarationIndex, -1)}
                              className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                                getActiveItemIndex(declarationIndex) === -1
                                  ? 'bg-secondary font-semibold px-4'
                                  : 'px-2'
                              }`}
                            >
                              {isSpanish ? 'Todos' : 'All'}
                            </button>
                            {/* Individual Item Tabs */}
                            {declaration.partidas.map((item, itemIndex) => (
                              <button
                                key={itemIndex}
                                onClick={() => setActiveItemIndex(declarationIndex, itemIndex)}
                                className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 px-4 whitespace-nowrap flex-shrink-0 ${
                                  getActiveItemIndex(declarationIndex) === itemIndex
                                    ? 'bg-secondary font-semibold'
                                    : ''
                                }`}
                              >
                                {getDeclarationSummaryTranslation('item', isSpanish)} #{itemIndex + 1}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Show items based on active tab */}
                      <div className='flex flex-col'>
                        {declaration.partidas && (() => {
                          const currentActiveItemIndex = getActiveItemIndex(declarationIndex);
                          const itemLabel = (idx) => isSpanish ? `Partida #${idx + 1}` : `Item #${idx + 1}`;
                          const itemBreadcrumb = (idx) => `${exportLabel} > ${itemLabel(idx)}`;
                          return currentActiveItemIndex === -1
                            ? // Show all items
                              declaration.partidas.map((item, itemIndex) => (
                          <div key={itemIndex} id={`section-declaration-summary-export-${declarationIndex}-item-${itemIndex}`} data-nav-breadcrumb={itemBreadcrumb(itemIndex)} className='split-table border-container p-4 rounded-md mt-4'>
                            <ItemInformation editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} item={item} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} />
                            <div id={`section-declaration-summary-export-${declarationIndex}-item-${itemIndex}-documents`} data-nav-breadcrumb={itemBreadcrumb(itemIndex)}>
                              <DocumentsTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} item={item} />
                            </div>
                            <div id={`section-declaration-summary-export-${declarationIndex}-item-${itemIndex}-packages`} data-nav-breadcrumb={itemBreadcrumb(itemIndex)}>
                              <PackagesTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} item={item} />
                            </div>
                          </div>
                              ))
                            : // Show only active item
                              declaration.partidas[currentActiveItemIndex] && (
                                <div id={`section-declaration-summary-export-${declarationIndex}-item-${currentActiveItemIndex}`} data-nav-breadcrumb={itemBreadcrumb(currentActiveItemIndex)} className='split-table border-container p-4 rounded-md mt-4'>
                                  <ItemInformation editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} item={declaration.partidas[currentActiveItemIndex]} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} />
                                  <div id={`section-declaration-summary-export-${declarationIndex}-item-${currentActiveItemIndex}-documents`} data-nav-breadcrumb={itemBreadcrumb(currentActiveItemIndex)}>
                                    <DocumentsTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} item={declaration.partidas[currentActiveItemIndex]} />
                                  </div>
                                  <div id={`section-declaration-summary-export-${declarationIndex}-item-${currentActiveItemIndex}-packages`} data-nav-breadcrumb={itemBreadcrumb(currentActiveItemIndex)}>
                                    <PackagesTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} item={declaration.partidas[currentActiveItemIndex]} />
                                  </div>
                                </div>
                              );
                        })()}
                      </div>
                      </>
                      )}
                    </div>
                      );
                    })
                : // Show only active declaration
                  editableData[activeDeclarationIndex] && (() => {
                    const declaration = editableData[activeDeclarationIndex];
                    const declarationIndex = activeDeclarationIndex;
                    const exportLabel = `${getDeclarationSummaryTranslation(getDeclarationKind(declaration), isSpanish)} #${declarationIndex + 1}`;
                    return (
                    <div key={declarationIndex} id={`section-declaration-summary-export-${declarationIndex}`} data-nav-breadcrumb={exportLabel} className="mt-2 mb-2 border-t pt-4 first:border-t-0 first:pt-0">
                      <div className="flex items-center justify-between">
                      <h2 className={`text-4xl font-semibold split-header uppercase text-template-primary ${!declaration.expediente && 'text-red-600'}`}>
                        {getDeclarationSummaryTranslation(getDeclarationKind(declaration), isSpanish)} #{declarationIndex + 1}
                      </h2>

                      {/* Declaration-Level Documents Toggle */}
                      <div className="">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <button
                              type="button"
                              onClick={() => handleToggleDeclarationDocuments(declarationIndex)}
                              className={`text-sm flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                                hasDeclarationDocuments[declarationIndex]
                                  ? 'bg-primary text-white'
                                  : 'bg-secondary text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {hasDeclarationDocuments[declarationIndex] && (
                                <Check className="w-4 h-4" />
                              )}
                              <span>
                                {!isSpanish ? "Add Documents at Export Level" : "Agregar Documentos en Nivel de Exportación"}
                              </span>
                            </button>
                            {(hasDeclarationDocuments[declarationIndex]) && (
                              <button
                                type="button"
                                onClick={() => handleToggleRemoveDuplicates(declarationIndex)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                                  removeDuplicates[declarationIndex]
                                    ? 'bg-primary text-white'
                                    : 'bg-secondary text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {removeDuplicates[declarationIndex] && (
                                  <Check className="w-4 h-4" />
                                )}
                                <span>
                                  {!isSpanish ? "Remove Duplicates" : "Eliminar Duplicados"}
                                </span>
                              </button>
                            )}
                          </div>
                          {(hasDeclarationDocuments[declarationIndex]) && (
                            <span className="text-sm text-gray-600">
                              {declaration.documentos?.length || 0} {!isSpanish ? "documents" : "documentos"}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleExportCollapsed(declarationIndex)}
                        className="flex-shrink-0 p-2 rounded-full hover:bg-gray-100 transition-colors"
                        title={collapsedExportByIndex[declarationIndex] ? (isSpanish ? 'Expandir' : 'Expand') : (isSpanish ? 'Colapsar' : 'Collapse')}
                        aria-expanded={!collapsedExportByIndex[declarationIndex]}
                      >
                        {collapsedExportByIndex[declarationIndex] ? (
                          <ChevronRight className="size-5 text-gray-600" />
                        ) : (
                          <ChevronDown className="size-5 text-gray-600" />
                        )}
                      </button>
                      </div>

                      {!collapsedExportByIndex[declarationIndex] && (
                      <>
                      <DeclarationInformation 
                        handleChange={handleChange} 
                        declaration={declaration} 
                        declarationIndex={declarationIndex} 
                        isSpanish={isSpanish}
                        setEditableData={setEditableData}
                        setIsDirty={setIsDirty}
                      />

                      {/* Declaration-Level Documents */}
                      {declaration.documentos && Array.isArray(declaration.documentos) && declaration.documentos.length > 0 && (
                        <DeclarationDocumentsTable
                          editableData={editableData}
                          setEditableData={setEditableData}
                          setIsDirty={setIsDirty}
                          declarationIndex={declarationIndex}
                          isSpanish={isSpanish}
                          declaration={declaration}
                        />
                      )}

                      {/* Items (Partidas) - single declaration view */}
                      <div className="form-group flex-1 mt-6 !mb-0 flex justify-between flex-row items-center">
                        <label htmlFor="partidas" className="!mb-0">{getDeclarationSummaryTranslation('items', isSpanish)} ({declaration.partidas?.length || 0})</label>
                        <button
                          className="py-1 text-xs flex items-center gap-1"
                          onClick={() => {
                            const updatedData = addPartida(editableData, declarationIndex);
                            if (updatedData !== editableData) {
                              setEditableData(updatedData);
                              setIsDirty(true);
                            }
                          }}
                        >
                          <Plus className={`size-4`} />
                          {getDeclarationSummaryTranslation('addItem', isSpanish)}
                        </button>
                      </div>

                      {/* Item Tabs Container */}
                      {declaration.partidas && declaration.partidas.length > 0 && (
                        <div className="border-ta pt-4 mt-4 overflow-x-auto">
                          <div className="flex gap-2 items-center pb-2">
                            {/* All Tab */}
                            <button
                              onClick={() => setActiveItemIndex(declarationIndex, -1)}
                              className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                                getActiveItemIndex(declarationIndex) === -1
                                  ? 'bg-secondary font-semibold px-4'
                                  : 'px-2'
                              }`}
                            >
                              {isSpanish ? 'Todos' : 'All'}
                            </button>
                            {/* Individual Item Tabs */}
                            {declaration.partidas.map((item, itemIndex) => (
                              <button
                                key={itemIndex}
                                onClick={() => setActiveItemIndex(declarationIndex, itemIndex)}
                                className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 px-4 whitespace-nowrap flex-shrink-0 ${
                                  getActiveItemIndex(declarationIndex) === itemIndex
                                    ? 'bg-secondary font-semibold'
                                    : ''
                                }`}
                              >
                                {getDeclarationSummaryTranslation('item', isSpanish)} #{itemIndex + 1}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Show items based on active tab */}
                      <div className='flex flex-col'>
                        {declaration.partidas && (() => {
                          const currentActiveItemIndex = getActiveItemIndex(declarationIndex);
                          const itemLabel = (idx) => isSpanish ? `Partida #${idx + 1}` : `Item #${idx + 1}`;
                          const itemBreadcrumb = (idx) => `${exportLabel} > ${itemLabel(idx)}`;
                          return currentActiveItemIndex === -1
                            ? // Show all items
                              declaration.partidas.map((item, itemIndex) => (
                          <div key={itemIndex} id={`section-declaration-summary-export-${declarationIndex}-item-${itemIndex}`} data-nav-breadcrumb={itemBreadcrumb(itemIndex)} className='split-table border-container p-4 rounded-md mt-4'>
                            <ItemInformation editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} item={item} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} />
                            <div id={`section-declaration-summary-export-${declarationIndex}-item-${itemIndex}-documents`} data-nav-breadcrumb={itemBreadcrumb(itemIndex)}>
                              <DocumentsTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} item={item} />
                            </div>
                            <div id={`section-declaration-summary-export-${declarationIndex}-item-${itemIndex}-packages`} data-nav-breadcrumb={itemBreadcrumb(itemIndex)}>
                              <PackagesTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} item={item} />
                            </div>
                          </div>
                              ))
                            : // Show only active item
                              declaration.partidas[currentActiveItemIndex] && (
                                <div id={`section-declaration-summary-export-${declarationIndex}-item-${currentActiveItemIndex}`} data-nav-breadcrumb={itemBreadcrumb(currentActiveItemIndex)} className='split-table border-container p-4 rounded-md mt-4'>
                                  <ItemInformation editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} item={declaration.partidas[currentActiveItemIndex]} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} />
                                  <div id={`section-declaration-summary-export-${declarationIndex}-item-${currentActiveItemIndex}-documents`} data-nav-breadcrumb={itemBreadcrumb(currentActiveItemIndex)}>
                                    <DocumentsTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} item={declaration.partidas[currentActiveItemIndex]} />
                                  </div>
                                  <div id={`section-declaration-summary-export-${declarationIndex}-item-${currentActiveItemIndex}-packages`} data-nav-breadcrumb={itemBreadcrumb(currentActiveItemIndex)}>
                                    <PackagesTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} item={declaration.partidas[currentActiveItemIndex]} />
                                  </div>
                                </div>
                              );
                        })()}
                      </div>
                      </>
                      )}
                    </div>
                    );
                  })()
            )}
          </div>
        )}
      </div>
    </div>
  );
};